import React, { useEffect, useState } from "react";
import {
  Button,
  EditProps,
  SimpleForm,
  useDataProvider,
  useRedirect,
} from "react-admin";
import {
  Grid,
  Paper,
  Box,
  Stepper,
  Step,
  StepLabel,
  DialogTitle,
  DialogContent,
  Typography,
  DialogActions,
  Dialog,
  CircularProgress,
  Backdrop,
} from "@material-ui/core";
import { useStyles } from "./style";
import RoutePlanMap from "./components/RoutePlanMap";
import { useDispatch } from "react-redux";
import { changeSelectedRoute } from "../../redux/navmode.action";
import PermissionLoading from "../../components/PermissionLoading";
import {
  colors,
  getSelectedRoutePlanVersion,
  processRoutesToAddSchoolLocation,
  processRoutesToRemoveSchoolLocation,
  setSelectedRoutePlanVersion,
  versioningRoutePlan,
} from "./tools";
import HeaderContent from "../../components/layout/components/HeaderContent";
import StepperNavigation from "./components/StepperNavigation";
import OptimiseRouteSection from "./components/OptimiseRouteSection";
import FinalPreviewSection from "./components/FinalPreviewSection";
import WaveInformationAndEffectiveDateSection from "./components/WaveInformationAndEffectiveDateSection";
import SanitizedGrid from "../../components/SanitizedGrid";
import Alert from "@material-ui/lab/Alert";
import TableResultRouteCreation, {
  IRecord,
} from "./components/TableResultRouteCreation";
import { find, findIndex } from "lodash";
import CustomSplit from "../../components/CustomSplit";
import LoadingProgressComponent from "./components/LoadingProgressComponent";

const EnhancedRoutePlanEdit = (props: EditProps) => {
  const { id } = props;
  const classes = useStyles();

  const [open, setOpen] = useState(false);
  const [oldRouteIds, setOldRouteIds] = useState<number[]>([]);
  const [recordToSave, setRecordToSave] = useState<any>(null);
  const [results, setResults] = useState<IRecord[]>([]);
  const [processingRoute, setProcessingRoute] = useState<boolean>(false);
  const [isSaveButtonClicked, setIsSaveButtonClicked] =
    useState<boolean>(false);

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingSave, setLoadingSave] = useState(false);
  const [formData, setFormData] = useState<any>(null);

  const [isLoadingMap, setIsLoadingMap] = useState<boolean>(false);
  const [progressMap, setProgressMap] = useState(1);

  const provider = useDataProvider();
  const redirect = useRedirect();
  const dispatch = useDispatch();

  const breadcrumb = [
    { link: "/enhancedBusSchedulePlans", title: "AI Route Plans" },
    { link: `/enhancedBusSchedulePlans/${id}`, title: id },
  ];

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleOpenDialog = () => {
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setProcessingRoute(false);
    setIsSaveButtonClicked(false);
    setLoadingSave(false);
  };

  const handleFinish = () => {
    redirect("/enhancedBusSchedulePlans");
  };

  const isContainsProcessedData = (results: IRecord[]) => {
    return results.find(
      (result) =>
        result.recordStatus?.includes("Loading...") ||
        result.recordStatus?.includes("Retry"),
    );
  };

  const getContent = (activeStep: number) => {
    switch (activeStep) {
      case 0:
        return <WaveInformationAndEffectiveDateSection />;
      case 1:
        return <OptimiseRouteSection mode="update" progressMap={progressMap} />;
      case 2:
        return <FinalPreviewSection />;
      default:
        return <></>;
    }
  };

  const transformRoute = (route: any) => {
    return {
      id: route.realRouteId,
      name: route.routeName,
      startTime: route.startTime,
      endTime: route.endTime,
      driverId: route.driverId,
      busAssistantId: route.busAssistantId,
      vehicleId: route.vehicleId,
      backupDriverIds: route.backupDriverIds,
      backupBusAssistantIds: route.backupBusAssistantIds,
      backupVehicleIds: route.backupVehicleIds,
      assignedSubcontractor: route.assignedSubcontractor,
      routeGeometries: route.routeGeometries,
      routeLocations: route.routeLocations.map((loc: any) => {
        return {
          ...loc,
          id: loc.id,
          arrivedAt: loc.arrivalTime,
          studentIds: loc.students.map((student: any) => student.id),
        };
      }),
    };
  };

  const transformBusSchedulePlan = (busSchedulePlan: any) => {
    return {
      id: busSchedulePlan.id,
      nextEffectiveDate: busSchedulePlan.nextEffectiveDate,
      maxVehicleCapacity: busSchedulePlan.maxVehicleCapacity,
      maxStopDuration: busSchedulePlan.maxStopTime,
      desiredRouteDuration: busSchedulePlan.desiredRouteDuration,
    };
  };

  const fetchUpdateRoute = async (
    route: any,
    busSchedulePlanId: number,
    index: number,
    retryCount: number = 0,
  ) => {
    try {
      const data = transformRoute(route);

      await provider.customUpdate(
        `busSchedulePlans/${busSchedulePlanId}/routes/${data.id}`,
        {
          data: data,
        },
      );

      results[index].recordStatus = "Success";
      setResults([...results]);
    } catch (err: any) {
      retryCount++;

      const errorMessage = err.message;
      if (retryCount <= 5 && !errorMessage.includes("Success")) {
        results[index].recordStatus = `Retry... (${retryCount}/5)`;
        setResults([...results]);

        // give random delay between 0.5 - 1 seconds
        const delay = Math.floor(Math.random() * 1000) + 500;
        setTimeout(() => {
          fetchUpdateRoute(route, busSchedulePlanId, index, retryCount);
        }, delay);

        return;
      }

      results[index].recordStatus = `Error, ${errorMessage}`;
      setResults([...results]);
    }
  };

  const handleUpdateRoutes = async (
    routes: any[],
    busSchedulePlanId: number,
  ) => {
    for (const route of routes) {
      const index = routes.indexOf(route);

      results[index].recordStatus = "Loading...";

      setResults([...results]);

      await fetchUpdateRoute(route, busSchedulePlanId, index);
    }
  };

  const handleSave = async () => {
    try {
      setIsSaveButtonClicked(true);
      setLoadingSave(true);

      const { data: busSchedulePlan } = await provider.update(
        `busSchedulePlans`,
        {
          id: recordToSave.id,
          data: transformBusSchedulePlan(recordToSave),
          previousData: transformBusSchedulePlan(recordToSave),
        },
      );

      await handleDeleteOldRoutesWhenReadjustRoutes(oldRouteIds);

      if (recordToSave.routes.length > 0) {
        await handleUpdateRoutes(
          recordToSave.routes,
          Number(busSchedulePlan.id),
        );
      } else {
        setLoadingSave(false);
        handleFinish();
      }
    } catch (err: any) {
      results.forEach((result) => {
        result.recordStatus = "Error, failed update route plan";
      });

      setResults([...results]);
      setLoadingSave(false);
    }
  };

  const handleRetry = async (selectedRecord: any) => {
    if (selectedRecord) {
      const index = findIndex(results, {
        recordDescription: selectedRecord.recordDescription,
      });
      const findRecord = find(recordToSave.routes, {
        routeName: selectedRecord.recordDescription,
      });

      if (index < 0 || !findRecord) {
        return;
      }

      results[index].recordStatus = "Loading...";

      setResults([...results]);

      await fetchUpdateRoute(findRecord, formData.id, index);
    }
  };

  const handleConfirmationSave = (values: any) => {
    handleOpenDialog();

    setRecordToSave({
      ...values,
      routes: processRoutesToRemoveSchoolLocation(
        values.routes,
        values.busSchedule.type,
      ),
    });
    setOldRouteIds(values.oldRouteIds);
  };

  const handleDeleteOldRoutesWhenReadjustRoutes = async (
    routeIds: number[],
  ) => {
    if (routeIds) {
      for (const id of routeIds) {
        if (id !== 0) {
          await provider.customDelete(`routes/${id}`, { data: {} });
        }
      }
    }
  };

  const getBusSchedulePlanData = async () => {
    let data: any = {};

    setLoading(true);
    const { data: busSchedulePlanData } = await provider.getOne(
      "busSchedulePlans",
      {
        id,
      },
    );

    data.id = busSchedulePlanData.id;
    data.createdAt = busSchedulePlanData.createdAt;
    data.updatedAt = busSchedulePlanData.updatedAt;
    data.nextEffectiveDate = busSchedulePlanData.nextEffectiveDate;

    const { data: busScheduleData } = await provider.getOne("busSchedules", {
      id: busSchedulePlanData.busScheduleId,
    });
    data.busSchedule = busScheduleData;

    const { data: schoolData } = await provider.getOne("schools", {
      id: busScheduleData.schoolId,
    });
    data.school = schoolData;

    data.busScheduleId = busSchedulePlanData.busScheduleId;
    data.nextEffectiveDate = busSchedulePlanData.nextEffectiveDate;

    data.desiredRouteDuration = busSchedulePlanData.desiredRouteDuration;
    data.maxVehicleCapacity = busSchedulePlanData.maxVehicleCapacity;
    data.maxStopTime = busSchedulePlanData.maxStopDuration;

    const transformRoutes = processRoutesToAddSchoolLocation(
      busSchedulePlanData.nextRoutePlan.routes,
      busScheduleData,
      schoolData,
    );
    data.routes = transformRoutes.map((route: any, index: number) => {
      return {
        ...route,
        id: index + 1,
        realRouteId: route.id,
        routeName: route.name,
        routeGeometries: route.routeGeometries ?? [],
        routeColor: colors[index % colors.length], // if colors are not enough, roll over the colors array to first index
        backupDriverIds: route.backupDrivers.map((driver: any) => driver.id),
        backupBusAssistantIds: route.backupBusAssistants.map(
          (busAssistant: any) => busAssistant.id,
        ),
        backupVehicleIds: route.backupVehicles.map(
          (vehicle: any) => vehicle.id,
        ),
        routeLocations: route.routeLocations.map((loc: any, index: number) => {
          let locationCodeIndex;

          if (busScheduleData.type === "to_school") {
            locationCodeIndex = index;
          } else {
            locationCodeIndex = index - 1;
          }

          // convert ASCII Code into Alphabet
          // 65 === A
          const locationCode = String.fromCharCode(65 + locationCodeIndex);

          return {
            ...loc,
            studentIds: loc.students
              ? loc.students.map((student: any) => student.id)
              : [],
            students: loc.students.map((student: any, index: number) => {
              return {
                ...student,
                locationCode: `${locationCode}${index + 1}`,
              };
            }),
          };
        }),
      };
    });
    data.droppedLocations = [];

    setLoading(false);
    return data;
  };

  useEffect(() => {
    dispatch(changeSelectedRoute(null));

    getBusSchedulePlanData()
      .then((data) => {
        setFormData(data);

        // only create version if there is no version
        const stored = localStorage.getItem("routePlanVersion");
        if (!stored) {
          // create original version of route plan
          versioningRoutePlan(data.routes, data.droppedLocations);
        }
      })
      .catch((error) => {
        console.log(error);
      });

    // reset the selected version to original version when page reloaded
    const selectedVersion = getSelectedRoutePlanVersion();
    if (selectedVersion && selectedVersion.id !== "Original") {
      setSelectedRoutePlanVersion("Original");
    }
  }, []);

  useEffect(() => {
    if (activeStep === 2 && recordToSave?.routes.length > 0) {
      const tempResults = recordToSave.routes.map((record: any) => {
        return {
          recordDescription: record.routeName,
          recordStatus: "Pending",
        };
      });
      setResults(tempResults);
    }
  }, [recordToSave, activeStep]);

  useEffect(() => {
    if (isContainsProcessedData(results)) {
      setProcessingRoute(true);
    } else {
      setProcessingRoute(false);
    }
  }, [results]);

  if (loading) {
    return (
      <Box height="500px">
        <PermissionLoading />
      </Box>
    );
  }

  return (
    <>
      <HeaderContent
        breadcrumb={breadcrumb}
        headerTitle={`AI Route Plan #${id}`}
      />

      <Paper style={{ marginTop: "18px" }}>
        <SimpleForm
          className={classes.simpleForm}
          initialValues={{
            routes: [],
            droppedLocations: [],
            oldRouteIds: [],
          }}
          record={formData}
          toolbar={null}
          save={handleConfirmationSave}
        >
          <CustomSplit
            sizes={[50, 50]}
            minSize={[700, 300]}
            expandToMin={false}
            gutterSize={10}
            gutterAlign="center"
            snapOffset={30}
            dragInterval={1}
            direction="horizontal"
            cursor="col-resize"
          >
            <div style={{ padding: "24px 34px" }}>
              <Stepper activeStep={activeStep} alternativeLabel>
                <Step>
                  <StepLabel>Wave Information and Effective Date</StepLabel>
                </Step>
                <Step>
                  <StepLabel>Routes</StepLabel>
                </Step>
                <Step>
                  <StepLabel>Final Review</StepLabel>
                </Step>
              </Stepper>

              <StepperNavigation
                activeStep={activeStep}
                handleNext={handleNext}
                handleBack={handleBack}
                mode="update"
              />

              <div style={{ padding: "12px 14px" }}>
                {getContent(activeStep)}
              </div>
            </div>
            <div style={{ padding: 0 }}>
              <RoutePlanMap
                mode="update"
                setIsLoadingMap={setIsLoadingMap}
                setProgressMap={setProgressMap}
              />
            </div>
          </CustomSplit>

          <Backdrop className={classes.backdropLoading} open={isLoadingMap}>
            <LoadingProgressComponent value={progressMap} />
          </Backdrop>
        </SimpleForm>
      </Paper>

      <Dialog
        open={open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className={classes.dialog}
        maxWidth="lg"
      >
        <DialogTitle id="alert-dialog-title">
          Route Plan Saving Progress
        </DialogTitle>
        <DialogContent>
          <SanitizedGrid container spacing={2}>
            {isSaveButtonClicked && recordToSave.routes.length > 0 ? (
              <>
                <Grid item xs={12}>
                  <Alert severity="warning" style={{ margin: "0 16px" }}>
                    Please wait for the process to complete, do not close the
                    page.
                  </Alert>
                </Grid>
                <Grid item xs={12}>
                  <TableResultRouteCreation
                    records={results}
                    handleRetry={handleRetry}
                  />
                </Grid>
              </>
            ) : (
              <Grid item xs={12}>
                <Typography variant="body1">
                  Are you sure to save the route plan?
                </Typography>
              </Grid>
            )}
          </SanitizedGrid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="secondary" label="Back" />

          {isSaveButtonClicked && recordToSave.routes.length > 0 ? (
            <Button
              onClick={handleFinish}
              color="primary"
              label="Done"
              disabled={processingRoute}
            />
          ) : (
            <Button onClick={handleSave} color="primary" label="Save">
              {loadingSave && (
                <CircularProgress
                  style={{ color: "#FFFFFF", width: "20px", height: "20px" }}
                />
              )}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EnhancedRoutePlanEdit;
